import React from "react";

import axios from "axios";
import FacebookLogin, {
  type ReactFacebookFailureResponse,
  type ReactFacebookLoginInfo,
} from "react-facebook-login";
import { serializeError } from "serialize-error";

import fbLogo from "@assets/fb-logo.svg";
import { useUser } from "@components/user-context";
import PartnerConsentSwitch from "@features/partner-consent/shared/partner-consent-switch";
import { type User } from "@internal/user";
import {
  ConnectionType,
  Partner,
  PartnerPermissionEnum,
} from "@north-beam/nb-client-side";
import { formatDate, formatting } from "@north-beam/nb-common";
import {
  ConnectionCard,
  type PartnerConsentComponentProps,
} from "@pages/settings/connection-card";
import {
  ConnectionStatusType,
  type LoginComponentChildrenProps,
  type LoginComponentWithFrontendIntegrationProps,
} from "@pages/settings/connection-status-manager";

const type = ConnectionType.integration_facebook_ads;

const PartnerConsent = ({
  hideWhenMissingConsent,
}: PartnerConsentComponentProps) => (
  <PartnerConsentSwitch
    partner={Partner.META}
    permissions={[PartnerPermissionEnum.data_exports]}
    label="Share Northbeam Attribution Data with Meta"
    hideWhenMissingConsent={hideWhenMissingConsent}
  />
);

export function FBConnectionCard({
  integrationDetails,
}: {
  integrationDetails: any;
}) {
  const { user } = useUser();
  const successfulConnectionExtraText = React.useCallback(() => {
    return (
      <>
        <p className="card-text">
          <em>Note: if you want to update these details, please contact us!</em>
        </p>
      </>
    );
  }, []);

  return (
    <ConnectionCard
      type={type}
      connectionTypeName="Facebook Ads"
      connectionCallToAction={[
        "Integrate your Facebook Ads data to start receiving insights about your Facebook ad",
        "spend. Connecting your Facebook account will not modify any of your existing ads or",
        "revert them to the learning phase. New ads will have UTMs added automatically, which",
        "may reset them to the learning phase. Please contact Northbeam customer success if",
        "you want to disable auto-appending UTMs for Facebook Ads (or any other platform).",
      ].join(" ")}
      logoUrl={fbLogo}
      logoAltText="Facebook Ads Logo"
      successfulConnectionExtraText={successfulConnectionExtraText}
      partnerConsentComponent={PartnerConsent}
      knowledgeBaseLink="https://docs.northbeam.io/docs/setting-up-facebook-ads-tracking"
      supportMultipleAccounts="subAccounts"
    >
      {({ setStatus, accounts }: LoginComponentChildrenProps) => (
        <FacebookLoginComponent
          setStatus={setStatus}
          user={user}
          accounts={accounts}
          integrationDetails={integrationDetails}
        />
      )}
    </ConnectionCard>
  );
}

function FacebookLoginComponent({
  setStatus,
  integrationDetails,
  user,
}: LoginComponentWithFrontendIntegrationProps & { readonly user: User }) {
  const facebookScopes = integrationDetails.facebookScopes || {};

  const callback = React.useCallback(
    async (response: ReactFacebookLoginInfo | ReactFacebookFailureResponse) => {
      if ("accessToken" in response) {
        const accessToken = response.accessToken;
        setStatus({ type: ConnectionStatusType.FETCHING_AVAILABLE_ACCOUNTS });
        try {
          const adAccounts = await fetchFacebookAdAccounts(accessToken);

          const accountList = adAccounts.map(({ name, id, insights }) => {
            let descriptionHTML = "";
            if (insights) {
              const insight = insights.data[0];
              const spend = formatting.formatCurrency.approximate(
                insight.spend,
                { code: user.currencies.currentCurrency },
              );
              const impressions = formatting.formatInteger.approximate(
                insight.impressions,
                {},
              );
              const startDate = formatDate(insight.date_start);
              const endDate = formatDate(insight.date_end);
              descriptionHTML = `${spend} spent, ${impressions} impressions between ${startDate} and ${endDate}`;
            }
            return {
              name,
              id,
              descriptionHTML,
              data: {
                access_token: accessToken,
                account_id: id,
              },
            };
          });

          setStatus({
            type: ConnectionStatusType.SELECTING_ACCOUNT,
            accountList,
          });
        } catch (e) {
          setStatus({
            type: ConnectionStatusType.HAS_ERROR,
            errorMessage: JSON.stringify(serializeError(e)),
          });
        }
      } else {
        setStatus({
          type: ConnectionStatusType.HAS_ERROR,
          errorMessage: `Could not connect to the Facebook API. Error: ${response.status}`,
        });
      }
    },
    [setStatus, user],
  );

  return (
    <FacebookLogin
      appId={integrationDetails.facebookAppId}
      autoLoad={false}
      fields="name,email,picture"
      scope={Object.keys(facebookScopes).join(",")}
      cssClass="button primary-button sm"
      textButton="CONNECT TO FACEBOOK ADS"
      callback={callback}
    />
  );
}

async function fetchFacebookAdAccounts(accessToken: string) {
  const rv: any[] = [];

  let url =
    "https://graph.facebook.com/v16.0/me/adaccounts?fields=name,insights&limit=50";

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    // eslint-disable-next-line prefer-spread
    rv.push.apply(rv, response.data.data);

    if (response.data.paging?.next) {
      url = response.data.paging?.next;
    } else {
      break;
    }
  }

  return rv;
}
